import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  PRESET_INSET_LABELS,
  clampOpeningPosition,
  ROOM_FEATURE_KINDS,
  ROOM_FEATURE_LABELS,
  ROOM_OPENING_KINDS,
  ROOM_OPENING_LABELS,
  ROOM_SHAPE_PRESETS,
  flattenOutline,
  insertShapeVertex,
  pointsBounds,
  removeShapeVertex,
  setWallBulge,
  setWallLength,
  shapeFromPreset,
  shapeFromWalls,
  wallAt,
  wallLength,
  wallsFromOutline,
  type RoomFeature,
  type RoomFeatureKind,
  type RoomOpening,
  type RoomOpeningKind,
  type RoomShapePreset,
  type TypedWall,
} from '@peles/shared';
import type { RoomShapeEditorState } from './useRoomShapeEditor';

export const inputClass = 'w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm';
export const labelClass = 'block text-xs font-medium text-gray-600 mb-1';
const buttonClass =
  'inline-flex items-center justify-center gap-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50';
const primaryClass =
  'inline-flex items-center justify-center gap-1 rounded-md bg-primary-600 px-3 py-1.5 text-sm text-white hover:bg-primary-700 disabled:opacity-50';

const num = (v: string, fallback = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
};

export const PRESET_LABELS: Record<RoomShapePreset, string> = {
  RECTANGLE: 'Rectangle',
  L_SHAPE: 'L shape',
  U_SHAPE: 'U shape',
  T_SHAPE: 'T shape',
  CUT_CORNERS: 'Cut corners',
  ROUNDED_END: 'Rounded end',
  CIRCLE: 'Round',
};

/** Size a new feature starts at, before anyone drags it. */
export const FEATURE_DEFAULTS: Record<
  RoomFeatureKind,
  Pick<RoomFeature, 'shape' | 'widthFt' | 'heightFt'>
> = {
  PILLAR: { shape: 'RECT', widthFt: 2, heightFt: 2 },
  BAR: { shape: 'RECT', widthFt: 12, heightFt: 3 },
  STAGE: { shape: 'RECT', widthFt: 20, heightFt: 12 },
  DANCE_FLOOR: { shape: 'RECT', widthFt: 20, heightFt: 20 },
  RAISED: { shape: 'RECT', widthFt: 15, heightFt: 10 },
  SUNKEN: { shape: 'RECT', widthFt: 15, heightFt: 10 },
  NO_GO: { shape: 'RECT', widthFt: 6, heightFt: 6 },
};

/** A new opening's width and which way it opens. */
export const OPENING_DEFAULTS: Record<
  RoomOpeningKind,
  Pick<RoomOpening, 'widthFt' | 'swing' | 'hinge'>
> = {
  DOOR: { widthFt: 3, swing: 'IN', hinge: 'START' },
  DOUBLE_DOOR: { widthFt: 6, swing: 'IN' },
  FIRE_EXIT: { widthFt: 6, swing: 'OUT', hinge: 'START' },
  SERVICE: { widthFt: 4, swing: 'IN', hinge: 'START' },
  WINDOW: { widthFt: 6 },
};

// ─── Shape presets ──────────────────────────────────────────────────

export function PresetPanel({ editor }: { editor: RoomShapeEditorState }) {
  const box = pointsBounds(flattenOutline(editor.shape.outline));
  const [preset, setPreset] = useState<RoomShapePreset>('RECTANGLE');
  const [dims, setDims] = useState({
    widthFt: String(Math.round(box.width) || 60),
    heightFt: String(Math.round(box.height) || 40),
    insetWidthFt: '',
    insetDepthFt: '',
  });
  const insets = PRESET_INSET_LABELS[preset];

  const apply = () => {
    const shape = shapeFromPreset(preset, {
      widthFt: num(dims.widthFt, 60),
      heightFt: num(dims.heightFt, 40),
      insetWidthFt: dims.insetWidthFt ? num(dims.insetWidthFt) : undefined,
      insetDepthFt: dims.insetDepthFt ? num(dims.insetDepthFt) : undefined,
    });
    editor.replaceOutline(shape.outline);
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">
        Start from a shape, then drag corners or type walls to match the room.
      </p>
      <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Starting shape">
        {ROOM_SHAPE_PRESETS.map((p) => (
          <button
            key={p}
            type="button"
            role="radio"
            aria-checked={preset === p}
            onClick={() => setPreset(p)}
            className={`rounded-md border px-2 py-1.5 text-sm ${
              preset === p
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-gray-300 text-gray-700'
            }`}
          >
            {PRESET_LABELS[p]}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label>
          <span className={labelClass}>{preset === 'CIRCLE' ? 'Diameter (ft)' : 'Width (ft)'}</span>
          <input
            className={inputClass}
            type="number"
            min={2}
            value={dims.widthFt}
            onChange={(e) => setDims({ ...dims, widthFt: e.target.value })}
          />
        </label>
        {preset !== 'CIRCLE' && (
          <label>
            <span className={labelClass}>Depth (ft)</span>
            <input
              className={inputClass}
              type="number"
              min={2}
              value={dims.heightFt}
              onChange={(e) => setDims({ ...dims, heightFt: e.target.value })}
            />
          </label>
        )}
        {insets?.[0] && (
          <label>
            <span className={labelClass}>{insets[0]} (ft)</span>
            <input
              className={inputClass}
              type="number"
              min={1}
              value={dims.insetWidthFt}
              onChange={(e) => setDims({ ...dims, insetWidthFt: e.target.value })}
            />
          </label>
        )}
        {insets?.[1] && (
          <label>
            <span className={labelClass}>{insets[1]} (ft)</span>
            <input
              className={inputClass}
              type="number"
              min={1}
              value={dims.insetDepthFt}
              onChange={(e) => setDims({ ...dims, insetDepthFt: e.target.value })}
            />
          </label>
        )}
      </div>
      <button type="button" className={primaryClass} onClick={apply}>
        Use this shape
      </button>
    </div>
  );
}

// ─── Typed walls ────────────────────────────────────────────────────

/**
 * Walk the room with a tape: each wall's length, then which way it turns.
 * The fastest way in when there is no drawing — and the gap it reports is
 * how far off the measurements are.
 */
export function WallsPanel({ editor }: { editor: RoomShapeEditorState }) {
  const [walls, setWalls] = useState<TypedWall[]>(() =>
    editor.shape.outline.length >= 3
      ? wallsFromOutline(editor.shape.outline)
      : [
          { lengthFt: 60, turnDeg: 90 },
          { lengthFt: 40, turnDeg: 90 },
          { lengthFt: 60, turnDeg: 90 },
          { lengthFt: 40, turnDeg: 90 },
        ],
  );
  const { gapFt } = shapeFromWalls(walls);
  const set = (i: number, patch: Partial<TypedWall>) =>
    setWalls((ws) => ws.map((w, j) => (j === i ? { ...w, ...patch } : w)));

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">
        Start in the top-left corner facing along the top wall. Type each wall and the turn at its
        end.
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500">
            <th className="w-8 font-medium">#</th>
            <th className="font-medium">Length (ft)</th>
            <th className="font-medium">Then turn (°)</th>
            <th className="w-8" />
          </tr>
        </thead>
        <tbody>
          {walls.map((w, i) => (
            <tr key={i}>
              <td className="text-gray-500">{i + 1}</td>
              <td className="pr-1 py-0.5">
                <input
                  className={inputClass}
                  type="number"
                  min={0}
                  aria-label={`Wall ${i + 1} length`}
                  value={w.lengthFt}
                  onChange={(e) => set(i, { lengthFt: num(e.target.value) })}
                />
              </td>
              <td className="pr-1 py-0.5">
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    aria-label={`Wall ${i + 1} turns ${w.turnDeg < 0 ? 'left' : 'right'}`}
                    title="Switch between a left and a right turn"
                    className="w-7 rounded-md border border-gray-300 py-1 text-xs font-medium text-gray-700"
                    onClick={() => set(i, { turnDeg: -w.turnDeg })}
                  >
                    {w.turnDeg < 0 ? 'L' : 'R'}
                  </button>
                  <input
                    className={inputClass}
                    type="number"
                    min={0}
                    max={180}
                    aria-label={`Wall ${i + 1} turn angle`}
                    value={Math.abs(w.turnDeg)}
                    onChange={(e) => {
                      const deg = Math.min(180, Math.max(0, num(e.target.value)));
                      set(i, { turnDeg: w.turnDeg < 0 ? -deg : deg });
                    }}
                  />
                </div>
              </td>
              <td>
                <button
                  type="button"
                  aria-label={`Remove wall ${i + 1}`}
                  className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30"
                  disabled={walls.length <= 3}
                  onClick={() => setWalls((ws) => ws.filter((_, j) => j !== i))}
                >
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        type="button"
        className={buttonClass}
        onClick={() => setWalls((ws) => [...ws, { lengthFt: 10, turnDeg: 90 }])}
      >
        <Plus size={14} /> Add wall
      </button>
      {gapFt > 0.05 && (
        <p className="rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800" role="status">
          The last wall ends {gapFt} ft from where the first began — a measurement is off, or a wall
          is missing.
        </p>
      )}
      <button
        type="button"
        className={primaryClass}
        onClick={() => editor.replaceOutline(shapeFromWalls(walls).outline)}
      >
        Draw these walls
      </button>
    </div>
  );
}

// ─── Features ───────────────────────────────────────────────────────

export function FeaturesPanel({ editor }: { editor: RoomShapeEditorState }) {
  const box = pointsBounds(flattenOutline(editor.shape.outline));
  const add = (kind: RoomFeatureKind) => {
    const d = FEATURE_DEFAULTS[kind];
    editor.addFeature({
      kind,
      ...d,
      x: Math.round(box.minX + box.width / 2 - d.widthFt / 2),
      y: Math.round(box.minY + box.height / 2 - d.heightFt / 2),
    });
  };
  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">
        What is part of the room itself — it appears in every plan drawn here. Pillars, a bar and a
        stage come off the usable floor.
      </p>
      <div className="grid grid-cols-2 gap-2">
        {ROOM_FEATURE_KINDS.map((k) => (
          <button key={k} type="button" className={buttonClass} onClick={() => add(k)}>
            <Plus size={14} /> {ROOM_FEATURE_LABELS[k]}
          </button>
        ))}
      </div>
      {editor.shape.features.length > 0 && (
        <ul className="divide-y divide-gray-100 rounded-md border border-gray-200 text-sm">
          {editor.shape.features.map((f) => (
            <li key={f.id}>
              <button
                type="button"
                className={`w-full px-2 py-1.5 text-left ${
                  editor.selection?.type === 'feature' && editor.selection.id === f.id
                    ? 'bg-primary-50'
                    : ''
                }`}
                onClick={() => editor.setSelection({ type: 'feature', id: f.id })}
              >
                {f.label || ROOM_FEATURE_LABELS[f.kind]}{' '}
                <span className="text-xs text-gray-500">
                  {f.widthFt}×{f.heightFt} ft
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Doors & windows ────────────────────────────────────────────────

export function OpeningsPanel({ editor }: { editor: RoomShapeEditorState }) {
  const [kind, setKind] = useState<RoomOpeningKind>('DOUBLE_DOOR');
  const wall = editor.selection?.type === 'wall' ? editor.selection.index : null;
  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">
        Click a wall on the drawing, then add a door or window to it. Drag it along the wall to
        place it.
      </p>
      <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Opening type">
        {ROOM_OPENING_KINDS.map((k) => (
          <button
            key={k}
            type="button"
            role="radio"
            aria-checked={kind === k}
            onClick={() => setKind(k)}
            className={`rounded-md border px-2 py-1.5 text-sm ${
              kind === k
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-gray-300 text-gray-700'
            }`}
          >
            {ROOM_OPENING_LABELS[k]}
          </button>
        ))}
      </div>
      <button
        type="button"
        className={primaryClass}
        disabled={wall === null}
        onClick={() =>
          wall !== null &&
          editor.addOpening({ kind, segment: wall, position: 0.5, ...OPENING_DEFAULTS[kind] })
        }
      >
        {wall === null ? 'Select a wall first' : `Add to wall ${wall + 1}`}
      </button>
      {editor.shape.openings.length > 0 && (
        <ul className="divide-y divide-gray-100 rounded-md border border-gray-200 text-sm">
          {editor.shape.openings.map((o) => (
            <li key={o.id}>
              <button
                type="button"
                className={`w-full px-2 py-1.5 text-left ${
                  editor.selection?.type === 'opening' && editor.selection.id === o.id
                    ? 'bg-primary-50'
                    : ''
                }`}
                onClick={() => editor.setSelection({ type: 'opening', id: o.id })}
              >
                {o.label || ROOM_OPENING_LABELS[o.kind]}{' '}
                <span className="text-xs text-gray-500">
                  wall {o.segment + 1} · {o.widthFt} ft
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── The selected thing ─────────────────────────────────────────────

function NumberField({
  label,
  value,
  onChange,
  min,
  step,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min?: number;
  step?: number;
}) {
  return (
    <label>
      <span className={labelClass}>{label}</span>
      <input
        className={inputClass}
        type="number"
        min={min}
        step={step ?? 0.5}
        value={Math.round(value * 100) / 100}
        onChange={(e) => {
          const n = parseFloat(e.target.value);
          if (Number.isFinite(n)) onChange(n);
        }}
      />
    </label>
  );
}

function Choice<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { id: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div>
      <span className={labelClass}>{label}</span>
      <div className="flex gap-1" role="radiogroup" aria-label={label}>
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={value === o.id}
            onClick={() => onChange(o.id)}
            className={`flex-1 rounded-md border px-2 py-1 text-xs ${
              value === o.id
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-gray-300 text-gray-700'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Properties of whatever is selected on the drawing. */
export function SelectionPanel({ editor }: { editor: RoomShapeEditorState }) {
  const { shape, selection, update } = editor;
  if (!selection) {
    return (
      <p className="text-xs text-gray-500">
        Select a corner, wall, feature or door on the drawing to change it.
      </p>
    );
  }

  if (selection.type === 'vertex') {
    const v = shape.outline[selection.index];
    if (!v) return null;
    return (
      <div className="space-y-2" data-testid="selection-vertex">
        <h4 className="text-sm font-medium text-gray-800">Corner {selection.index + 1}</h4>
        <div className="grid grid-cols-2 gap-2">
          <NumberField
            label="X (ft)"
            value={v.x}
            onChange={(x) =>
              update((s) => ({
                ...s,
                outline: s.outline.map((p, i) => (i === selection.index ? { ...p, x } : p)),
              }))
            }
          />
          <NumberField
            label="Y (ft)"
            value={v.y}
            onChange={(y) =>
              update((s) => ({
                ...s,
                outline: s.outline.map((p, i) => (i === selection.index ? { ...p, y } : p)),
              }))
            }
          />
        </div>
        <button
          type="button"
          className={buttonClass}
          disabled={shape.outline.length <= 3}
          onClick={() => {
            update((s) => removeShapeVertex(s, selection.index));
            editor.setSelection(null);
          }}
        >
          <Trash2 size={14} /> Remove corner
        </button>
      </div>
    );
  }

  if (selection.type === 'wall') {
    if (selection.index >= shape.outline.length) return null;
    const { a, b, bulge } = wallAt(shape.outline, selection.index);
    return (
      <div className="space-y-2" data-testid="selection-wall">
        <h4 className="text-sm font-medium text-gray-800">Wall {selection.index + 1}</h4>
        <NumberField
          label={bulge ? 'Straight length (ft)' : 'Length (ft)'}
          value={Math.hypot(b.x - a.x, b.y - a.y)}
          min={0.5}
          onChange={(len) => update((s) => setWallLength(s, selection.index, len))}
        />
        {bulge !== 0 && (
          <p className="text-xs text-gray-500">
            Along the curve: {Math.round(wallLength(a, b, bulge) * 10) / 10} ft
          </p>
        )}
        <label className="block">
          <span className={labelClass}>Curve</span>
          <input
            type="range"
            className="w-full"
            min={-1}
            max={1}
            step={0.05}
            aria-label="Wall curve"
            value={bulge}
            onChange={(e) =>
              update((s) => setWallBulge(s, selection.index, Number(e.target.value)), {
                coalesce: true,
              })
            }
            onPointerUp={editor.endGesture}
            onKeyUp={editor.endGesture}
          />
        </label>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={buttonClass}
            onClick={() => {
              update((s) => insertShapeVertex(s, selection.index));
              editor.setSelection({ type: 'vertex', index: selection.index + 1 });
            }}
          >
            <Plus size={14} /> Split wall
          </button>
          {bulge !== 0 && (
            <button
              type="button"
              className={buttonClass}
              onClick={() => update((s) => setWallBulge(s, selection.index, 0))}
            >
              Straighten
            </button>
          )}
        </div>
      </div>
    );
  }

  if (selection.type === 'feature') {
    const f = shape.features.find((x) => x.id === selection.id);
    if (!f) return null;
    const patch = (p: Partial<RoomFeature>) => editor.updateFeature(f.id, p);
    return (
      <div className="space-y-2" data-testid="selection-feature">
        <h4 className="text-sm font-medium text-gray-800">{ROOM_FEATURE_LABELS[f.kind]}</h4>
        <label className="block">
          <span className={labelClass}>Label</span>
          <input
            className={inputClass}
            value={f.label ?? ''}
            onChange={(e) => patch({ label: e.target.value || undefined })}
          />
        </label>
        <Choice
          label="Shape"
          value={f.shape}
          options={[
            { id: 'RECT', label: 'Square' },
            { id: 'CIRCLE', label: 'Round' },
          ]}
          onChange={(shape) => patch({ shape })}
        />
        <div className="grid grid-cols-2 gap-2">
          <NumberField
            label="Width (ft)"
            value={f.widthFt}
            min={0.5}
            onChange={(widthFt) => patch({ widthFt: Math.max(0.5, widthFt) })}
          />
          <NumberField
            label="Depth (ft)"
            value={f.heightFt}
            min={0.5}
            onChange={(heightFt) => patch({ heightFt: Math.max(0.5, heightFt) })}
          />
          <NumberField label="X (ft)" value={f.x} onChange={(x) => patch({ x })} />
          <NumberField label="Y (ft)" value={f.y} onChange={(y) => patch({ y })} />
          <NumberField
            label="Rotation (°)"
            value={f.rotation ?? 0}
            step={15}
            onChange={(rotation) => patch({ rotation })}
          />
        </div>
        <button type="button" className={buttonClass} onClick={() => editor.removeFeature(f.id)}>
          <Trash2 size={14} /> Remove
        </button>
      </div>
    );
  }

  const o = shape.openings.find((x) => x.id === selection.id);
  if (!o) return null;
  const patch = (p: Partial<RoomOpening>) => editor.updateOpening(o.id, p);
  const isWindow = o.kind === 'WINDOW';
  return (
    <div className="space-y-2" data-testid="selection-opening">
      <h4 className="text-sm font-medium text-gray-800">
        {ROOM_OPENING_LABELS[o.kind]} · wall {o.segment + 1}
      </h4>
      <label className="block">
        <span className={labelClass}>Label</span>
        <input
          className={inputClass}
          value={o.label ?? ''}
          onChange={(e) => patch({ label: e.target.value || undefined })}
        />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <NumberField
          label="Width (ft)"
          value={o.widthFt}
          min={1}
          onChange={(widthFt) => patch({ widthFt: Math.max(1, widthFt) })}
        />
        <NumberField
          label="Position (%)"
          value={o.position * 100}
          step={1}
          onChange={(p) => {
            const { a, b, bulge } = wallAt(shape.outline, o.segment);
            patch({ position: clampOpeningPosition(wallLength(a, b, bulge), o.widthFt, p / 100) });
          }}
        />
      </div>
      {!isWindow && (
        <>
          <Choice
            label="Opens"
            value={o.swing ?? 'IN'}
            options={[
              { id: 'IN', label: 'Into the room' },
              { id: 'OUT', label: 'Outward' },
            ]}
            onChange={(swing) => patch({ swing })}
          />
          {o.kind !== 'DOUBLE_DOOR' && (
            <Choice
              label="Hinge"
              value={o.hinge ?? 'START'}
              options={[
                { id: 'START', label: 'This side' },
                { id: 'END', label: 'Other side' },
              ]}
              onChange={(hinge) => patch({ hinge })}
            />
          )}
        </>
      )}
      <button type="button" className={buttonClass} onClick={() => editor.removeOpening(o.id)}>
        <Trash2 size={14} /> Remove
      </button>
    </div>
  );
}
